import React from 'react';
import _ from 'underscore';
import { t } from 'services/translation';
import { titleize } from 'lib/string_utils';
import pluralize from 'pluralize';
import D13nStore from 'stores/plugins/d13n';
import NavigationActionCreator from 'action_creators/navigation';
import ModalActionCreator from 'action_creators/modal';
import Popup from 'views/shared/ui/popup';
import Cover from 'views/plugins/checkout/mobile/screens/cover';

class ConfirmationMobileSummary extends React.PureComponent {
  static displayName = 'ConfirmationMobile-MessageScreen-Plugins';

  _transitionToMessage = () => {
    NavigationActionCreator.transitionBack();
  };

  _bookingFee = () => {
    if (this.props.pricing.includes_booking_fee) {
      const fee = this.props.pricing.booking_fee.total_price;
      return (
        <li>
          <span className="label">{titleize(t('SERVICE_FEE'))}</span>
          <span className="value">{fee}</span>
        </li>
      );
    }
  };

  _additionalTax = () => {
    if (this.props.pricing.includes_additional_tax) {
      return (
        <li>
          <span className="label">
            {this.props.pricing.additional_tax_label}
          </span>
          <span className="value">
            {roundPrice(this.props.additional_tax_formatted)}
          </span>
        </li>
      );
    }
  };

  _cashPaymentNotice = () => {
    if (this.props.pricing.paid_in_cash) {
      return (
        <li>
          <span className="cashPaymentNotice">
            {t('AMOUNT_DUE_IN_CASH_AT_YOUR_ARRIVAL_ON')}
            <br />
            <strong>{this.props.date}</strong>.
          </span>
        </li>
      );
    }
  };

  _guestsPricing = () => {
    return (
      <li>
        <span className="label">
          {this.props.pricing.guests_pricing.price_per_guest} x{' '}
          {this.props.pricing.guests_pricing.number_of_guests}{' '}
          {pluralize(
            D13nStore.d('guest'),
            this.props.pricing.guests_pricing.number_of_guests
          )}
        </span>
        <span className="value">
          {this.props.pricing.guests_pricing.total_price}
        </span>
      </li>
    );
  };

  _addonPricing = (addonProps, index) => {
    return (
      <li>
        <span className="addonName">{addonProps.description}</span>
        <span className="label">
          <span className="addonPrice">{addonProps.price_per_guest} x </span>
          <span className="addonGuests">
            {addonProps.number_of_guests}{' '}
            {pluralize(D13nStore.d('guest_addon'), addonProps.number_of_guests)}
          </span>
        </span>
        <span className="value">{addonProps.total_price}</span>
      </li>
    );
  };

  _addonsPricing = () => {
    const self = this;

    return _.map(this.props.pricing.addons_pricing, (addon, index) => {
      return self._addonPricing(addon, index);
    });
  };

  _discountPricing = () => {
    if (this.props.pricing.includes_discount) {
      return (
        <li>
          <span className="label">
            {t('DISCOUNT')}:{this.props.pricing.discount_pricing.description}
          </span>
          <span className="value">
            {this.props.pricing.discount_pricing.total_price}
            <span className="toggleIconContainer">
              (<i className="icon af-minus-ar" />)
            </span>
          </span>
        </li>
      );
    }
  };

  _depositDue = () => {
    return (
      <li>
        <span className="label">{titleize(t('DEPOSIT_DUE'))}</span>
        <span className="value">
          {this.props.pricing.deposit_pricing.total_price}
        </span>
      </li>
    );
  };

  _showCancellationPolicyPopup = (e) => {
    e.preventDefault();
    ModalActionCreator.show({
      content: this._cancellationPolicyPopupContent(),
      triggeringElement: e.currentTarget,
      className: 'small ConfirmationSummary-CancellationPolicy-MobileModal',
    });
  };

  _cancellationPolicyPopupContent = () => {
    return (
      <div className="CancellationPolicy-Modal-content">
        <div>
          <b>{t('CANCELLATION_POLICY')}</b>
        </div>
        <hr />
        <p>{this.props.cancellation_policy}</p>
      </div>
    );
  };

  _renderCancellationPolicyTrigger = () => {
    return (
      <a
        href="#"
        className="dotted"
        onClick={this._showCancellationPolicyPopup}
      >
        {_.str.truncate(this.props.short_cancellation_policy, 40)}
      </a>
    );
  };

  _renderCancellationPolicy = () => {
    if (!this.props.pricing.paid_in_cash) {
      return (
        <li className="separator">
          <span className="label">{t('CANCELLATION_POLICY')}</span>
          <span className="value condensed">
            {this.props.has_cancellation
              ? this._renderCancellationPolicyTrigger()
              : t('NO_CANCELLATIONS')}
          </span>
        </li>
      );
    }
  };

  _onlineDiscountPricing = () => {
    if (this.props.pricing.includes_online_discount) {
      return (
        <li>
          <span className="label">{t('ONLINE_PAYMENT_DISCOUNT')}:</span>
          <span className="value">
            {this.props.pricing.online_discount}
            <span className="toggleIconContainer">
              (<i className="icon af-minus-ar" />)
            </span>
          </span>
        </li>
      );
    }
  };

  _coverProps = () => {
    return {
      forInvoice: this.props.for_invoice,
      tourPicture: this.props.tour_picture,
      guideAvatar: this.props.guide_avatar,
      tourName: this.props.tour_name_translation,
      guideName: this.props.guide_full_name,
    };
  };

  render() {
    return (
      <div className="ConfirmationMobile-Screen ConfirmationMobile-Summary">
        <div className="ConfirmationMobile-ScreenHeader transition-ctx">
          <i
            className="icon af-chevron back-button"
            onClick={this._transitionToMessage}
          />
          {t('BOOKING_DETAILS')}
        </div>
        <div className="ConfirmationMobile-ScreenContent transition-ctx">
          <Cover {...this._coverProps()} />
          <div className="ConfirmationMobile-PageContent">
            <ul className="ConfirmationMobile-PricingBreakdown">
              <li>
                <span className="label">{t('DATE')}</span>
                <span className="value">{this.props.date}</span>
              </li>
              <li>
                <span className="label">{t('TIME')}</span>
                <span className="value">{this.props.time}</span>
              </li>
              <li>
                <span className="label">{t('DURATION')}</span>
                <span className="value">{this.props.duration}</span>
              </li>
              <li>
                <span className="label">{t('NUMBER_OF_GUESTS')}</span>
                <span className="value">
                  {this.props.guests}{' '}
                  {pluralize(D13nStore.d('guest_addon'), this.props.guests)}
                </span>
              </li>
              {this._renderCancellationPolicy()}
              <li>
                <span className="label">{t('TOTAL')}</span>
                <span className="value">
                  <span className="large">
                    <strong>{this.props.pricing.subtotal}</strong>
                  </span>
                </span>
              </li>
              {this._guestsPricing()}
              {this._addonsPricing()}
              {this._discountPricing()}
              {this._additionalTax()}
              {this._onlineDiscountPricing()}

              {this._bookingFee()}

              {this._depositDue()}
              {this._cashPaymentNotice()}
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

export default ConfirmationMobileSummary;
